import type { ProcedureAssignee } from '@/types/cgt'

/** Display labels for the ProcedureStatus enum. */
export const PROCEDURE_STATUS_LABELS: Record<string, string> = {
  in_progress: 'In progress',
  pending_review: 'Pending review',
  completed: 'Completed',
  cancelled: 'Cancelled',
}

/** The sentinel a select uses for "no filter". Never sent to the server. */
export const ANY = 'all'

export interface ProcedureFilterState {
  status: string
  assigneeId: string
  createdFrom: string
  createdTo: string
  /** Kits and MES only (z8nrz7e71k); absent elsewhere. */
  customerId?: string
}

export interface ProcedureFilterChip {
  key: 'status' | 'assignee' | 'raised' | 'customer'
  label: string
  value: string
}

/**
 * The filters currently narrowing the procedures list, as chips.
 *
 * Derived from the filter state rather than tracked beside it: a second copy of
 * "what is filtered" is a second thing to forget to update when a filter is
 * added, and a stale chip is worse than none — it explains an empty table with
 * a reason that is not true.
 *
 * Pure, and out of the component, so the label rules below are testable.
 */
export function describeProcedureFilters(
  state: ProcedureFilterState,
  assignableUsers: readonly ProcedureAssignee[],
  /** [id, label] of the tenant's customers, to name the customer chip. */
  customers: readonly { value: string; label: string }[] = [],
): ProcedureFilterChip[] {
  const chips: ProcedureFilterChip[] = []

  if (state.customerId && state.customerId !== ANY) {
    chips.push({
      key: 'customer',
      label: 'Customer',
      // The id until the list loads: the chip must not look empty.
      value: customers.find((c) => c.value === state.customerId)?.label ?? state.customerId,
    })
  }

  if (state.status !== ANY && state.status !== '') {
    chips.push({
      key: 'status',
      label: 'Status',
      // Falls back to the raw value: an unknown status is still worth showing,
      // and a blank chip would read as a bug in the filter rather than in the data.
      value: PROCEDURE_STATUS_LABELS[state.status] ?? state.status,
    })
  }

  if (state.assigneeId !== ANY && state.assigneeId !== '') {
    chips.push({
      key: 'assignee',
      label: 'Assigned to',
      // Falls back to the id: a roster that has not loaded yet must not make
      // the chip look empty.
      value: assignableUsers.find((u) => u.id === state.assigneeId)?.name ?? state.assigneeId,
    })
  }

  const { createdFrom, createdTo } = state
  if (createdFrom || createdTo) {
    chips.push({
      key: 'raised',
      label: 'Raised',
      // Each half of the range reads on its own, because an open-ended range is
      // a normal thing to ask for and "2026-08-01 to " is not a sentence.
      value:
        createdFrom && createdTo
          ? `${createdFrom} to ${createdTo}`
          : createdFrom
            ? `on or after ${createdFrom}`
            : `on or before ${createdTo}`,
    })
  }

  return chips
}
